import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from "react";
import type { UserProfile, Conversation, Topic, ThemeName, BoardNote, Feedback, AIPersonality } from "../types";
import { DEFAULT_PERSONALITIES } from "../types";
import { getDefaultTopics } from "../lib/mock-data";
import { supabase } from "../../lib/supabase";
import { toast } from "sonner";
import useCalendarStore from "../../store/useCalendarStore";
import api from "../../api";

interface AppState {
  user: UserProfile | null;
  conversations: Conversation[];
  activeConversationId: string | null;
  topics: Topic[];
  theme: ThemeName;
  darkMode: boolean;
  historyOpen: boolean;
  boardOpen: boolean;
  knowledgeOpen: boolean;
  notes: BoardNote[];
  personalities: AIPersonality[];
  activeModel: string;
  students: any[];
  selectedStudentId: string | null;
  isAdminView: boolean;
}

interface AppContextType extends AppState {
  login: (email: string, password: string) => Promise<boolean>;
  logout: (clearSession?: boolean) => Promise<void>;
  completeSetup: (grade: string, course: string, batch: string) => Promise<void>;
  setTheme: (t: ThemeName) => void;
  setDarkMode: (v: boolean) => void;
  resetPassword: (current: string, next: string) => boolean;
  setHistoryOpen: (open: boolean) => void;
  setBoardOpen: (open: boolean) => void;
  setKnowledgeOpen: (open: boolean) => void;
  createConversation: () => Promise<string>;
  setActiveConversation: (id: string | null) => void;
  addMessage: (convId: string, msg: Conversation["messages"][0]) => Promise<void>;
  updateTopics: (topics: Topic[]) => void;
  togglePinConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  setMessageFeedback: (convId: string, msgId: string, feedback: Feedback) => Promise<void>;
  decrementCredits: (n?: number) => void;
  updateCredits: (n: number) => void;
  addNote: (note: BoardNote) => Promise<void>;
  updateNote: (id: string, patch: Partial<BoardNote>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  requestExpert: (convId: string, context: string) => Promise<void>;
  markAsRead: (convId: string, role: 'user' | 'expert') => Promise<void>;
  loadPersonalities: () => Promise<void>;
  loadConversations: (userId: string, role?: string, overrideTeamId?: string) => Promise<void>;
  setActiveModel: (model: string) => void;
  setSelectedStudentId: (id: string | null) => void;
  startExpertSession: (convId: string) => Promise<void>;
  closeExpertSession: (convId: string) => Promise<void>;
}

const BenchrexContext = createContext<AppContextType | null>(null);

const DEFAULT_CREDITS = 100;

export function ScholarsAnchorProvider({
  children,
  initialActiveConversationId,
  forcedDarkMode,
  onDarkModeChange,
  teamId,
  userId,
  forceConversationId,
  isAdminView = false
}: {
  children: ReactNode,
  initialActiveConversationId?: string | null,
  forcedDarkMode?: boolean,
  onDarkModeChange?: (v: boolean) => void,
  teamId?: string,
  userId?: string | null,
  forceConversationId?: string | null,
  isAdminView?: boolean
}) {
  const scholarsanchorTheme = useCalendarStore(s => s.scholarsanchorTheme);
  const setScholarsAnchorTheme = useCalendarStore(s => s.setScholarsAnchorTheme);
  const scholarsanchorHistoryOpen = useCalendarStore(s => s.scholarsanchorHistoryOpen);
  const setScholarsAnchorHistoryOpen = useCalendarStore(s => s.setScholarsAnchorHistoryOpen);
  const scholarsanchorBoardOpen = useCalendarStore(s => s.scholarsanchorBoardOpen);
  const setScholarsAnchorBoardOpen = useCalendarStore(s => s.setScholarsAnchorBoardOpen);
  const scholarsanchorKnowledgeOpen = useCalendarStore(s => s.scholarsanchorKnowledgeOpen);
  const setScholarsAnchorKnowledgeOpen = useCalendarStore(s => s.setScholarsAnchorKnowledgeOpen);
  const scholarsanchorActiveConversationId = useCalendarStore(s => s.scholarsanchorActiveConversationId);
  const setScholarsAnchorActiveConversationId = useCalendarStore(s => s.setScholarsAnchorActiveConversationId);
  const scholarsanchorActiveModel = useCalendarStore(s => s.scholarsanchorActiveModel);
  const setScholarsAnchorActiveModel = useCalendarStore(s => s.setScholarsAnchorActiveModel);
  const scholarsanchorSelectedUserId = useCalendarStore(s => s.scholarsanchorSelectedUserId);
  const setScholarsAnchorSelectedUserId = useCalendarStore(s => s.setScholarsAnchorSelectedUserId);
  const scholarsanchorActiveTab = useCalendarStore(s => s.scholarsanchorActiveTab);
  const setScholarsAnchorActiveTab = useCalendarStore(s => s.setScholarsAnchorActiveTab);
  const scholarsanchorConversations = useCalendarStore(s => s.scholarsanchorConversations);
  const setScholarsAnchorConversations = useCalendarStore(s => s.setScholarsAnchorConversations);
  const scholarsanchorNotes = useCalendarStore(s => s.scholarsanchorNotes);
  const setScholarsAnchorNotes = useCalendarStore(s => s.setScholarsAnchorNotes);
  const scholarsanchorPersonalities = useCalendarStore(s => s.scholarsanchorPersonalities);
  const setScholarsAnchorPersonalities = useCalendarStore(s => s.setScholarsAnchorPersonalities);
  const scholarsanchorStudents = useCalendarStore(s => s.scholarsanchorStudents);
  const setScholarsAnchorStudents = useCalendarStore(s => s.setScholarsAnchorStudents);
  const scholarsanchorIsAdminView = useCalendarStore(s => s.scholarsanchorIsAdminView);
  const setScholarsAnchorIsAdminView = useCalendarStore(s => s.setScholarsAnchorIsAdminView);
  const darkMode = useCalendarStore(s => s.darkMode);
  const setGlobalDarkMode = useCalendarStore(s => s.setDarkMode);

  const [state, setState] = useState<AppState>(() => {
    return {
      user: null,
      conversations: scholarsanchorConversations,
      activeConversationId: forceConversationId || scholarsanchorActiveConversationId || initialActiveConversationId || null,
      topics: getDefaultTopics(),
      theme: scholarsanchorTheme as ThemeName,
      darkMode: darkMode,
      historyOpen: scholarsanchorHistoryOpen,
      boardOpen: scholarsanchorBoardOpen,
      knowledgeOpen: scholarsanchorKnowledgeOpen,
      notes: scholarsanchorNotes,
      personalities: scholarsanchorPersonalities.length > 0 ? scholarsanchorPersonalities : DEFAULT_PERSONALITIES,
      activeModel: scholarsanchorActiveModel,
      students: scholarsanchorStudents,
      selectedStudentId: scholarsanchorSelectedUserId,
      isAdminView: isAdminView,
    };
  });

  // Use localStorage so all tabs in the same browser share the same session ID
  const [currentSessionId] = useState(() => {
    const saved = localStorage.getItem("scholarsanchor-session-id");
    if (saved) return saved;
    const newId = crypto.randomUUID();
    localStorage.setItem("scholarsanchor-session-id", newId);
    return newId;
  });

  useEffect(() => {
    // Only persist UI preferences (NOT conversations, messages, notes — those live in Supabase)
    const uiPrefs = {
      theme: state.theme,
      darkMode: state.darkMode,
      personalities: state.personalities,
      activeModel: state.activeModel,
    };
    sessionStorage.setItem("doubt-solver-ui-prefs", JSON.stringify(uiPrefs));
    // Clean up old full-state cache if present
    sessionStorage.removeItem("doubt-solver-state");
    sessionStorage.removeItem("mock-user");
  }, [state.theme, state.darkMode, state.personalities]);

  useEffect(() => {
    if (initialActiveConversationId) {
      setScholarsAnchorActiveConversationId(initialActiveConversationId);
    }
  }, [initialActiveConversationId]);

  useEffect(() => {
    if (forcedDarkMode !== undefined) {
      setGlobalDarkMode(forcedDarkMode);
    }
  }, [forcedDarkMode]);

  useEffect(() => {
    if (forceConversationId) {
      setScholarsAnchorActiveConversationId(forceConversationId);
    }
  }, [forceConversationId]);

  useEffect(() => {
    // If in Admin View, reload data when userId or teamId changes
    if (isAdminView && (userId || teamId)) {
      loadConversations(userId || "", "student", teamId);
      loadNotes(userId || "", teamId);
    }
  }, [isAdminView, userId, teamId]);

  useEffect(() => {
    setScholarsAnchorIsAdminView(isAdminView);
  }, [isAdminView]);

  // Session enforcement listener — set up AFTER logout is defined (see below).
  // We store a ref to it so the realtime callback always sees the latest version.
  const logoutRef = React.useRef<(clearSession?: boolean) => Promise<void>>(async () => { });

  useEffect(() => {
    const root = document.documentElement;
    if (state.theme === "lavender") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", state.theme);
    }
  }, [state.theme]);

  useEffect(() => {
    const root = document.documentElement;
    if (state.darkMode) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [state.darkMode]);

  const isInitializing = React.useRef(false);

  useEffect(() => {
    if (isAdminView && !state.user) {
      const adminProfile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      if (adminProfile.id) {
        setState(s => ({
          ...s,
          user: {
            id: adminProfile.id,
            auth_id: adminProfile.auth_id || adminProfile.id,
            name: adminProfile.name || "ScholarsAnchor",
            email: adminProfile.email || "",
            role: adminProfile.role || "super_admin",
            credits: 999999,
            setupComplete: true,
            grade: "N/A",
            course: "N/A",
            batch: "N/A",
            team_id: adminProfile.team_id || teamId || "",
            schema: "public"
          }
        }));
      }
    }
  }, [isAdminView, state.user, teamId]);

  // Sync auth state with Supabase
  useEffect(() => {
    if (isInitializing.current) return;
    isInitializing.current = true;

    console.log("Setting up auth listener...");

    const handleAuth = async (event: any, session: any) => {
      console.log("Auth event:", event);
      if (session?.user) {
        // Exclusively check scholarsanchor.benchrex_users for portal access
        let userSchema: 'public' = 'public';
        const { data: profile, error: profileError } = await supabase
          .from("benchrex_users")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();

        if (profile) {
          console.log("Profile loaded:", profile.role);
          setState(s => ({
            ...s,
            user: {
              id: profile.id,
              auth_id: session.user.id,
              name: profile.name || session.user.user_metadata?.name || session.user.email?.split('@')[0] || "User",
              email: session.user.email || "",
              role: profile.role || "student",
              credits: profile.credits ?? DEFAULT_CREDITS,
              setupComplete: !!profile.grade,
              grade: profile.grade || "",
              course: profile.course || "",
              batch: profile.batch || "",
              team_id: profile.team_id || "",
              schema: userSchema
            },
          }));

          // Sync with legacy RBAC system
          const roleMap: Record<string, number> = {
            'super_admin': 1,
            'branch_admin': 2,
            'batch_admin': 3,
            'course_admin': 4,
            'content_manager': 5,
            'inquiry_admin': 6,
            'attendance_manager': 7,
            'faculty': 8,
            'viewer': 9,
            'doubt_expert': 10
          };
          localStorage.setItem('admin_profile', JSON.stringify({
            id: profile.id,
            auth_id: session.user.id,
            name: profile.name,
            email: session.user.email,
            role: profile.role || "student",
            team_id: profile.team_id || "",
            grade: profile.grade,
            course: profile.course,
            batch: profile.batch,
            access_level: roleMap[profile.role] || 99
          }));

          loadConversations(profile.id, profile.role);
          loadNotes(profile.id);
          loadPersonalities();
          loadTopics(profile.team_id);

          if (profile.role === 'doubt_expert' || profile.role === 'super_admin' || profile.role === 'faculty') {
            loadStudents();
          }
        }
      } else if (event === 'SIGNED_OUT') {
        setState(s => ({ ...s, user: null, activeConversationId: null, conversations: [] }));
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Use setTimeout to ensure the lock is not held too long in the synchronous part of the listener
      setTimeout(() => handleAuth(event, session), 0);
    });

    return () => {
      subscription.unsubscribe();
      isInitializing.current = false;
    };
  }, []);

  const loadConversations = useCallback(async (userId: string, role?: string, overrideTeamId?: string) => {
    console.log("Loading conversations for user:", userId, "with role:", role);
    if (!userId) return;
    try {
      const isExpert = role === 'doubt_expert' || role === 'super_admin' || role === 'faculty';
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const teamId = overrideTeamId || profile.team_id;

      let query = supabase
        .from("conversations")
        .select("*, user:benchrex_users(name, email), messages(*)")
        .order("updated_at", { ascending: false });

      if (teamId && (isExpert || state.isAdminView)) {
        query = query.eq('team_id', teamId);
      }

      if (isExpert) {
        // Experts see their own OR any expert session
        query = query.or(`user_id.eq.${userId},is_expert_session.eq.true`);
      } else {
        query = query.eq("user_id", userId);
      }

      const { data: convs, error } = await query;

      if (error) throw error;

      if (convs) {
        const mappedConvs: Conversation[] = convs.map((c: any) => ({
          id: c.id,
          title: c.title,
          pinned: c.pinned,
          isExpertSession: c.is_expert_session,
          expertId: c.expert_id,
          userHasUnread: c.user_has_unread,
          expertHasUnread: c.expert_has_unread,
          userId: c.user_id,
          teamId: c.team_id,
          userName: c.user?.name,
          userEmail: c.user?.email,
          createdAt: new Date(c.created_at).getTime(),
          updatedAt: new Date(c.updated_at).getTime(),
          messages: (c.messages || []).map((m: any) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            timestamp: new Date(m.created_at).getTime(),
            feedback: m.feedback,
            sources: m.sources,
            attachments: m.attachments,
            tags: m.tags,
            topicMentions: m.topic_mentions,
            webSearch: m.web_search,
            personalityId: m.personality_id,
            senderName: m.sender_name,
          })).sort((a: any, b: any) => a.timestamp - b.timestamp)
        }));

        setScholarsAnchorConversations(mappedConvs);
        setState(s => ({ ...s, conversations: mappedConvs }));
      }
    } catch (err: any) {
      console.error("Failed to load conversations:", err);
      toast.error(`Failed to load history: ${err.message}`);
    }
  }, [setScholarsAnchorConversations, state.isAdminView]);

  const loadPersonalities = useCallback(async () => {
    try {
      const { data: personas, error } = await supabase
        .from("personalities")
        .select("id, name, model, system_instructions, description, icon, tool_web_search, tool_calendar_mgmt")
        .order("name", { ascending: true });

      if (error) throw error;

      if (personas) {
        const mapped: AIPersonality[] = personas.map((p: any) => ({
          id: p.id,
          name: p.name,
          model: p.model,
          systemInstructions: p.system_instructions,
          description: p.description,
          icon: p.icon,
          tool_web_search: p.tool_web_search,
          tool_code_interpreter: p.tool_code_interpreter,
          tool_image_gen: p.tool_image_gen,
          tool_calendar_mgmt: p.tool_calendar_mgmt,
          apiKey: p.api_key,
        }));
        setScholarsAnchorPersonalities(mapped);
        setState(s => ({ ...s, personalities: mapped }));
      }
    } catch (err: any) {
      console.error("Failed to load personalities:", err);
    }
  }, [setScholarsAnchorPersonalities]);

  // ─── Load real topics from the knowledge file map ─────────────────────────
  const loadTopics = useCallback(async (teamId?: string) => {
    const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
    const tid = teamId || profile.team_id;
    if (!tid) return;
    try {
      let mapData;
      // 1. Try to get the local file map
      try {
        const res = await api.get('/api/admin/file-map');
        mapData = res.data;
        if (mapData.error) {
          throw new Error(mapData.error);
        }
      } catch (err) {
        // 2. If it fails or is missing, sync it
        await api.post('/api/admin/file-map/sync');
        // Fetch again after syncing
        const res2 = await api.get('/api/admin/file-map');
        mapData = res2.data;
        if (mapData.error) {
          console.error("Failed to generate file map:", mapData.error);
          return;
        }
      }

      const folders: { id: string; name: string; parent_id: string | null }[] = mapData.folders || [];
      const files: { id: string; name: string; folder_id: string }[] = mapData.files || [];

      // Build topics: top-level folders = subjects, their children = chapters, files in chapter folders = sections
      const topics: Topic[] = [];
      const rootFolders = folders.filter(f => f.parent_id === null);

      rootFolders.forEach((subjectFolder, si) => {
        // Child folders of this root folder = chapters
        const chapterFolders = folders.filter(f => f.parent_id === subjectFolder.id);

        if (chapterFolders.length > 0) {
          chapterFolders.forEach((chapterFolder, ci) => {
            const chapterFiles = files.filter(f => f.folder_id === chapterFolder.id);
            const sections = chapterFiles.map(f => ({ id: f.id, name: f.name }));
            topics.push({
              id: `${subjectFolder.id}-${chapterFolder.id}`,
              subject: subjectFolder.name,
              chapter: chapterFolder.name,
              category: 'school' as const,
              sections: sections.length > 0 ? sections : undefined
            });
          });
        } else {
          // No child folders — treat direct files in the root folder as chapters
          const directFiles = files.filter(f => f.folder_id === subjectFolder.id);
          directFiles.forEach(file => {
            topics.push({
              id: file.id,
              subject: subjectFolder.name,
              chapter: file.name,
              category: 'school' as const,
            });
          });
        }
      });

      if (topics.length > 0) {
        setState(s => ({ ...s, topics }));
      }
    } catch (err) {
      console.error('Failed to load topics from file map:', err);
    }
  }, []);

  const loadNotes = useCallback(async (userId: string, conversationId?: string, overrideTeamId?: string) => {
    if (!userId) return;
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const teamId = overrideTeamId || profile.team_id;
      let query = supabase
        .from("benchrex_board_notes")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (conversationId) query = query.eq("conversation_id", conversationId);
      if (teamId) query = query.eq('team_id', teamId);

      const { data: notes, error } = await query;

      if (error) throw error;

      if (notes) {
        const mappedNotes: BoardNote[] = notes.map((n: any) => ({
          id: n.id,
          title: n.title,
          content: n.content,
          color: n.color,
          createdAt: new Date(n.created_at).getTime(),
          source: n.source,
          conversationId: n.conversation_id,
        }));

        setScholarsAnchorNotes(mappedNotes);
        setState(s => ({ ...s, notes: mappedNotes }));
      }
    } catch (err: any) {
      console.error("Failed to load notes:", err);
    }
  }, [setScholarsAnchorNotes]);

  const loadStudents = useCallback(async () => {
    console.log("Loading students list...");
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      const teamId = profile.team_id;

      const { data: students, error: stuError } = await supabase
        .from("benchrex_users")
        .select("id, name, email, role, grade, course, batch")
        .eq('team_id', teamId);

      if (stuError) throw stuError;

      if (students) {
        setScholarsAnchorStudents(students);
        setState(s => ({ ...s, students }));
      }
    } catch (err: any) {
      console.error("Failed to load students:", err);
    }
  }, [setScholarsAnchorStudents]);

  useEffect(() => {
    if (state.user) {
      loadStudents();
    }
  }, [state.user]);

  const login = useCallback(async (
    email: string,
    password: string,
  ) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("Supabase auth failed:", error.message);
        toast.error(error.message);
        return false;
      }

      // Exclusively check benchrex_users for portal access
      const { data: bProfile } = await supabase
        .from("benchrex_users")
        .select("id, team_id")
        .eq("id", data.user.id)
        .maybeSingle();

      const isAuthorized = !!bProfile;

      if (!isAuthorized) {
        console.warn("User not authorized for ScholarsAnchor portal");
        await supabase.auth.signOut();
        toast.error("You do not have access to the Benchrex portal. Please contact your administrator.");
        return false;
      }

      return true;
    } catch (err: any) {
      console.error("Login error:", err);
      toast.error(err.message || "An unexpected error occurred during login");
      return false;
    }
  }, []);

  const logout = useCallback(async (clearSession = true) => {
    const userId = state.user?.id;
    if (userId && clearSession) {
      // Clear session token from DB so next login on any device starts fresh
      await supabase
        .from("benchrex_users")
        .update({ current_session_id: null })
        .eq("id", userId);
    }
    await supabase.auth.signOut();
    setState((s) => ({ ...s, user: null, activeConversationId: null }));
    localStorage.removeItem('admin_profile');
    sessionStorage.removeItem("mock-user");
  }, [state.user?.id]);

  // Keep the ref up-to-date so the realtime callback never gets stale
  React.useEffect(() => {
    logoutRef.current = logout;
  });

  // ── Single-session enforcement ──────────────────────────────────────────────
  // Stamp DB with our session ID on login; listen for changes to kick us out.
  React.useEffect(() => {
    if (!state.user || state.user.id.startsWith("mock-")) return;

    const userId = state.user.id;
    const userSchema = state.user.schema || 'public';
    const tableName = userSchema === "scholarsanchor" ? "benchrex_users" : "users";

    // Newest login always wins — overwrite whatever is currently in DB.
    supabase
      .from(tableName)
      .update({ current_session_id: currentSessionId })
      .eq("id", userId)
      .then(({ error }) => {
        if (error) console.error("Failed to stamp session ID:", error.message);
      });

    // Subscribe to changes on this user row.
    const channel = supabase
      .channel(`session-guard:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: userSchema,
          table: tableName,
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          const incoming = payload.new?.current_session_id;
          if (incoming && incoming !== currentSessionId) {
            toast.error(
              "You've been signed out — another session started on a different device.",
              { duration: 6000 }
            );
            // Call via ref so we always use the latest logout (no stale closure)
            logoutRef.current(false); // false = don't clear DB (new device already wrote its ID)
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [state.user?.id, currentSessionId]);

  // ── ScholarsAnchor Realtime ──────────────────────────────────────────────────────
  // Listen for new messages and conversation updates (unread flags, status changes)
  React.useEffect(() => {
    if (!state.user || state.user.id.startsWith("mock-")) return;

    const userId = state.user.id;

    const channel = supabase
      .channel('scholarsanchor-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const updated = payload.new as any;
            // Only care if it's our conversation OR if we are an expert
            const isExpert = state.user?.role === 'doubt_expert' || state.user?.role === 'super_admin' || state.user?.role === 'faculty';

            if (updated.user_id === userId || isExpert) {
              setState(s => ({
                ...s,
                conversations: s.conversations.some(c => c.id === updated.id)
                  ? s.conversations.map(c => c.id === updated.id ? {
                    ...c,
                    title: updated.title,
                    pinned: updated.pinned,
                    isExpertSession: updated.is_expert_session,
                    expertId: updated.expert_id,
                    userHasUnread: updated.user_has_unread,
                    expertHasUnread: updated.expert_has_unread,
                    updatedAt: new Date(updated.updated_at).getTime()
                  } : c)
                  : [{
                    id: updated.id,
                    title: updated.title,
                    pinned: updated.pinned,
                    isExpertSession: updated.is_expert_session,
                    expertId: updated.expert_id,
                    userHasUnread: updated.user_has_unread,
                    expertHasUnread: updated.expert_has_unread,
                    userId: updated.user_id,
                    messages: [],
                    createdAt: new Date(updated.created_at).getTime(),
                    updatedAt: new Date(updated.updated_at).getTime()
                  }, ...s.conversations]
              }));
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload) => {
          const newMsg = payload.new as any;
          // Check if this message belongs to one of our active conversations
          setState(s => {
            const conv = s.conversations.find(c => c.id === newMsg.conversation_id);
            if (!conv) return s;

            // Avoid duplicate messages (e.g. if we sent it ourselves and already added it locally)
            if (conv.messages.some(m => m.id === newMsg.id)) return s;

            const mappedMsg = {
              id: newMsg.id,
              role: newMsg.role,
              content: newMsg.content,
              timestamp: new Date(newMsg.created_at).getTime(),
              feedback: newMsg.feedback,
              sources: newMsg.sources,
              attachments: newMsg.attachments,
              tags: newMsg.tags,
              topicMentions: newMsg.topic_mentions,
              webSearch: newMsg.web_search,
              personalityId: newMsg.personality_id,
              senderName: newMsg.sender_name,
            };

            return {
              ...s,
              conversations: s.conversations.map((c: Conversation) =>
                c.id === newMsg.conversation_id
                  ? { ...c, messages: [...c.messages, mappedMsg].sort((a, b) => a.timestamp - b.timestamp) }
                  : c
              )
            };
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [state.user?.id, state.user?.role]);

  const setDarkMode = (darkMode: boolean) => {
    setState((s) => ({ ...s, darkMode }));
    onDarkModeChange?.(darkMode);
  };

  // Mock password reset — just acknowledges. No real auth in demo.
  const resetPassword = (_current: string, _next: string) => true;

  const completeSetup = async (grade: string, course: string, batch: string) => {
    if (!state.user) return;

    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const { error } = await supabase
          .from("benchrex_users")
          .update({ grade, course, batch, updated_at: new Date().toISOString() })
          .eq("id", state.user.id);

        if (error) throw error;
      } catch (err: any) {
        console.error("Setup error:", err);
        toast.error("Failed to save profile details to cloud");
      }
    }

    setState((s) => ({
      ...s,
      user: s.user ? { ...s.user, grade, course, batch, setupComplete: true } : null,
    }));
    toast.success("Profile updated locally!");
  };

  const setTheme = useCallback((theme: ThemeName) => setScholarsAnchorTheme(theme), [setScholarsAnchorTheme]);
  const setHistoryOpen = useCallback((open: boolean) => setScholarsAnchorHistoryOpen(open), [setScholarsAnchorHistoryOpen]);
  const setBoardOpen = useCallback((open: boolean) => setScholarsAnchorBoardOpen(open), [setScholarsAnchorBoardOpen]);
  const setKnowledgeOpen = useCallback((open: boolean) => setScholarsAnchorKnowledgeOpen(open), [setScholarsAnchorKnowledgeOpen]);

  const createConversation = useCallback(async () => {
    const id = crypto.randomUUID();
    const conv: Conversation = {
      id,
      title: "New Chat",
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      userId: state.user?.id || "",
      teamId: state.user?.team_id || "",
      pinned: false,
    };

    // 1. Update local state immediately for UI responsiveness
    setScholarsAnchorConversations([conv, ...useCalendarStore.getState().scholarsanchorConversations]);
    setScholarsAnchorActiveConversationId(id);
    setState(s => ({ ...s, conversations: [conv, ...s.conversations], activeConversationId: id }));

    // 2. Persist to Supabase if not mock user
    if (state.user && !state.user.id.startsWith("mock-")) {
      const userId = state.user.id;
      const teamId = state.user.team_id;

      // Run persistence in background to avoid blocking the UI
      supabase
        .from("conversations")
        .insert({
          id,
          user_id: userId,
          title: conv.title,
          pinned: conv.pinned,
          team_id: teamId,
        })
        .then(({ error }) => {
          if (error) {
            console.error("Supabase insert error:", error);
            toast.error(`Failed to sync: ${error.message}`);
          }
        });
    }

    return id;
  }, [setScholarsAnchorConversations, setScholarsAnchorActiveConversationId, state.user]);

  const setActiveConversation = useCallback((id: string | null) =>
    setScholarsAnchorActiveConversationId(id), [setScholarsAnchorActiveConversationId]);

  const addMessage = useCallback(async (convId: string, msg: Conversation["messages"][0]) => {
    // 1. Update local state immediately for UI responsiveness
    const currentConvs = useCalendarStore.getState().scholarsanchorConversations as Conversation[];
    const updatedConvs = currentConvs.map((c: Conversation) =>
      c.id === convId
        ? {
          ...c,
          messages: [...c.messages, msg],
          title: c.messages.length === 0 && msg.role === "user"
            ? msg.content.slice(0, 50) + (msg.content.length > 50 ? "..." : "")
            : c.title,
          updatedAt: Date.now(),
        }
        : c
    );

    setScholarsAnchorConversations(updatedConvs);
    setState(s => ({ ...s, conversations: updatedConvs }));

    // 2. Persist to Supabase in background
    if (state.user && !state.user.id.startsWith("mock-")) {
      const userId = state.user.id;
      const defaultTeamId = state.user.team_id;

      (async () => {
        try {
          const activeConv = currentConvs.find((c: Conversation) => c.id === convId);
          const teamId = activeConv?.teamId || defaultTeamId;

          // If first message, update title
          if (activeConv && activeConv.messages.length === 0 && msg.role === "user") {
            const newTitle = msg.content.slice(0, 50) + (msg.content.length > 50 ? "..." : "");
            await supabase
              .from("conversations")
              .update({ title: newTitle, updated_at: new Date().toISOString() })
              .eq("id", convId);
          }

          const { error } = await supabase
            .from("messages")
            .insert({
              id: msg.id,
              conversation_id: convId,
              team_id: teamId,
              role: msg.role,
              content: msg.content,
              sources: (msg as any).sources ? (typeof (msg as any).sources === 'string' ? JSON.parse((msg as any).sources) : (msg as any).sources) : null,
              web_search: (msg as any).webSearch ? (typeof (msg as any).webSearch === 'string' ? JSON.parse((msg as any).webSearch) : (msg as any).webSearch) : null,
              personality_id: (msg as any).personalityId || null,
              tags: msg.tags || [],
              topic_mentions: msg.topicMentions || [],
              attachments: msg.attachments || [],
              created_at: new Date(msg.timestamp).toISOString(),
            });

          if (error) throw error;

          // Update conversation last activity
          await supabase
            .from("conversations")
            .update({ updated_at: new Date().toISOString() })
            .eq("id", convId);

        } catch (err) {
          console.error("Failed to persist message:", err);
        }
      })();
    }
  }, [setScholarsAnchorConversations, state.user]);

  const updateTopics = (topics: Topic[]) => setState((s) => ({ ...s, topics }));

  const togglePinConversation = async (id: string) => {
    const conv = (scholarsanchorConversations as Conversation[]).find((c: Conversation) => c.id === id);
    const newPinnedState = !conv?.pinned;

    // Update local state
    const updatedConvs = (scholarsanchorConversations as Conversation[]).map((c: Conversation) =>
      c.id === id ? { ...c, pinned: !c.pinned } : c
    );
    setScholarsAnchorConversations(updatedConvs);
    setState((s) => ({ ...s, conversations: updatedConvs }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        await supabase
          .from("conversations")
          .update({ pinned: newPinnedState })
          .eq("id", id);
      } catch (err) {
        console.error("Failed to toggle pin:", err);
      }
    }
  };

  const deleteConversation = async (id: string) => {
    // Update local state
    const updatedConvs = (scholarsanchorConversations as Conversation[]).filter((c: Conversation) => c.id !== id);
    setScholarsAnchorConversations(updatedConvs);
    setScholarsAnchorActiveConversationId(scholarsanchorActiveConversationId === id ? null : scholarsanchorActiveConversationId);
    setState((s) => ({
      ...s,
      conversations: updatedConvs,
      activeConversationId: s.activeConversationId === id ? null : s.activeConversationId,
    }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const { error } = await supabase
          .from("conversations")
          .delete()
          .eq("id", id);

        if (error) {
          console.error("Supabase delete error:", error);
          toast.error(`Failed to delete conversation: ${error.message}`);
        }
      } catch (err: any) {
        console.error("Failed to delete conversation:", err);
        toast.error(`Delete failed: ${err.message || 'Unknown error'}`);
      }
    }
  };



  const setMessageFeedback = async (convId: string, msgId: string, feedback: Feedback) => {
    // Update local state
    setState((s) => ({
      ...s,
      conversations: s.conversations.map((c: Conversation) =>
        c.id === convId
          ? {
            ...c,
            messages: c.messages.map((m: any) =>
              m.id === msgId ? { ...m, feedback } : m
            ),
          }
          : c
      ),
    }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        // Update message feedback column
        await supabase
          .from("messages")
          .update({ feedback })
          .eq("id", msgId);
      } catch (err) {
        console.error("Failed to set feedback:", err);
      }
    }
  };

  const decrementCredits = (n = 1) =>
    setState((s) => ({
      ...s,
      user: s.user
        ? { ...s.user, credits: Math.max(0, s.user.credits - n) }
        : null,
    }));

  const updateCredits = (n: number) =>
    setState((s) => ({
      ...s,
      user: s.user ? { ...s.user, credits: n } : null,
    }));

  const addNote = async (note: BoardNote) => {
    // Update local state
    setState((s) => ({ ...s, notes: [note, ...s.notes] }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        await supabase
          .from("benchrex_board_notes")
          .insert({
            id: note.id,
            user_id: state.user.id,
            conversation_id: note.conversationId || null,
            title: note.title,
            content: note.content,
            color: note.color,
            source: note.source || 'manual',
            team_id: state.user.team_id || null,
            created_at: new Date(note.createdAt).toISOString(),
          });
      } catch (err) {
        console.error("Failed to persist note:", err);
      }
    }
  };

  const updateNote = async (id: string, patch: Partial<BoardNote>) => {
    // Update local state
    setState((s) => ({
      ...s,
      notes: s.notes.map((n: BoardNote) => (n.id === id ? { ...n, ...patch } : n)),
    }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const dbPatch: any = {};
        if (patch.title !== undefined) dbPatch.title = patch.title;
        if (patch.content !== undefined) dbPatch.content = patch.content;
        if (patch.color !== undefined) dbPatch.color = patch.color;

        await supabase
          .from("benchrex_board_notes")
          .update(dbPatch)
          .eq("id", id);
      } catch (err) {
        console.error("Failed to update note:", err);
      }
    }
  };

  const deleteNote = async (id: string) => {
    // Update local state
    setState((s) => ({ ...s, notes: s.notes.filter((n: BoardNote) => n.id !== id) }));

    // Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        await supabase
          .from("benchrex_board_notes")
          .delete()
          .eq("id", id);
      } catch (err) {
        console.error("Failed to delete note:", err);
      }
    }
  };

  const requestExpert = useCallback(async (convId: string, context: string) => {
    // 1. Update local state
    setState(s => ({
      ...s,
      conversations: s.conversations.map((c: Conversation) =>
        c.id === convId ? { ...c, isExpertSession: true } : c
      )
    }));

    // 2. Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const { error } = await supabase
          .from("conversations")
          .update({
            is_expert_session: true,
            expert_id: state.user.id,
            updated_at: new Date().toISOString()
          })
          .eq("id", convId);

        if (error) throw error;

        // Add a system message about the request
        const expertMsg = {
          id: crypto.randomUUID(),
          role: "ai",
          content: `Expert Request: ${context}\n\nA human expert has been notified and will join this session shortly.`,
          timestamp: Date.now(),
          tags: ["System", "Expert Request"]
        };

        await addMessage(convId, expertMsg as any);
      } catch (err: any) {
        toast.error(`Expert request failed: ${err.message}`);
      }
    }
  }, [addMessage, state.user]);

  const startExpertSession = useCallback(async (convId: string) => {
    // 1. Update local state
    const currentConvs = useCalendarStore.getState().scholarsanchorConversations as Conversation[];
    const updatedConvs = currentConvs.map(c => 
      c.id === convId ? { 
        ...c, 
        isExpertSession: true,
        expertId: state.user?.id || undefined
      } : c
    );
    setScholarsAnchorConversations(updatedConvs);
    setState(s => ({ ...s, conversations: updatedConvs }));

    // 2. Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const { error } = await supabase
          .from("conversations")
          .update({ 
            is_expert_session: true,
            expert_id: state.user.id,
            expert_joined_at: new Date().toISOString(),
            updated_at: new Date().toISOString() 
          })
          .eq("id", convId);
        
        if (error) throw error;

        // Add a system message about the expert session starting
        const expertMsg = {
          id: crypto.randomUUID(),
          role: "ai",
          content: `${state.user.name || "An expert"} has started the expert session.`,
          timestamp: Date.now(),
          tags: ["System", "Expert Session Active"]
        };
        
        await addMessage(convId, expertMsg as any);
      } catch (err: any) {
        toast.error(`Failed to start expert session: ${err.message}`);
      }
    }
  }, [addMessage, setScholarsAnchorConversations, state.user]);

  const closeExpertSession = useCallback(async (convId: string) => {
    // 1. Update local state
    const currentConvs = useCalendarStore.getState().scholarsanchorConversations as Conversation[];
    const updatedConvs = currentConvs.map(c => 
      c.id === convId ? { 
        ...c, 
        isExpertSession: false,
        expertId: undefined
      } : c
    );
    setScholarsAnchorConversations(updatedConvs);
    setState(s => ({ ...s, conversations: updatedConvs }));

    // 2. Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const { error } = await supabase
          .from("conversations")
          .update({ 
            is_expert_session: false,
            expert_id: null,
            expert_joined_at: null,
            updated_at: new Date().toISOString() 
          })
          .eq("id", convId);
        
        if (error) throw error;

        // Add a system message about the expert session ending
        const systemMsg = {
          id: crypto.randomUUID(),
          role: "ai",
          content: `The expert session has been closed by ${state.user.name || "the expert"}.`,
          timestamp: Date.now(),
          tags: ["System", "Expert Session Closed"]
        };
        
        await addMessage(convId, systemMsg as any);
      } catch (err: any) {
        toast.error(`Failed to close expert session: ${err.message}`);
      }
    }
  }, [addMessage, setScholarsAnchorConversations, state.user]);

  const markAsRead = useCallback(async (convId: string, role: 'user' | 'expert') => {
    // 1. Check if update is needed
    const currentConvs = useCalendarStore.getState().scholarsanchorConversations as Conversation[];
    const conv = currentConvs.find(c => c.id === convId);
    if (!conv) return;

    const needsUpdate = (role === 'user' && conv.userHasUnread) || (role === 'expert' && conv.expertHasUnread);
    if (!needsUpdate) return;

    // 2. Update local state
    const updatedConvs = currentConvs.map((c: Conversation) =>
      c.id === convId
        ? {
          ...c,
          userHasUnread: role === 'user' ? false : c.userHasUnread,
          expertHasUnread: role === 'expert' ? false : c.expertHasUnread
        }
        : c
    );
    setScholarsAnchorConversations(updatedConvs);
    setState(s => ({ ...s, conversations: updatedConvs }));

    // 2. Persist to Supabase
    if (state.user && !state.user.id.startsWith("mock-")) {
      try {
        const update: any = {};
        if (role === 'user') update.user_has_unread = false;
        if (role === 'expert') update.expert_has_unread = false;

        await supabase
          .from("conversations")
          .update(update)
          .eq("id", convId);
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
  }, [setScholarsAnchorConversations, state.user]);

  const setActiveModel = useCallback((model: string) => {
    setState(s => ({ ...s, activeModel: model }));
    setScholarsAnchorActiveModel(model);
  }, [setScholarsAnchorActiveModel]);

  const setSelectedStudentId = useCallback((id: string | null) => {
    setState(s => ({ ...s, selectedStudentId: id }));
    setScholarsAnchorSelectedUserId(id);
  }, [setScholarsAnchorSelectedUserId]);

  const contextValue = useMemo(() => ({
    user: state.user,
    conversations: scholarsanchorConversations,
    activeConversationId: scholarsanchorActiveConversationId,
    topics: state.topics,
    theme: scholarsanchorTheme as ThemeName,
    darkMode: darkMode,
    historyOpen: scholarsanchorHistoryOpen,
    boardOpen: scholarsanchorBoardOpen,
    knowledgeOpen: scholarsanchorKnowledgeOpen,
    notes: scholarsanchorNotes,
    personalities: scholarsanchorPersonalities,
    activeModel: scholarsanchorActiveModel,
    students: scholarsanchorStudents,
    selectedStudentId: scholarsanchorSelectedUserId,
    isAdminView: scholarsanchorIsAdminView,
    login,
    logout,
    completeSetup,
    setTheme,
    setDarkMode: setGlobalDarkMode,
    resetPassword,
    setHistoryOpen,
    setBoardOpen,
    setKnowledgeOpen,
    createConversation,
    loadConversations,
    setActiveConversation,
    addMessage,
    updateTopics,
    togglePinConversation,
    deleteConversation,
    setMessageFeedback,
    decrementCredits,
    updateCredits,
    addNote,
    updateNote,
    deleteNote,
    requestExpert,
    loadPersonalities,
    markAsRead,
    setActiveModel,
    setSelectedStudentId,
    startExpertSession,
    closeExpertSession,
  }), [
    state.user, scholarsanchorConversations, scholarsanchorActiveConversationId, state.topics,
    scholarsanchorTheme, darkMode, scholarsanchorHistoryOpen, scholarsanchorBoardOpen,
    scholarsanchorKnowledgeOpen, scholarsanchorNotes, scholarsanchorPersonalities, scholarsanchorActiveModel,
    scholarsanchorStudents, scholarsanchorSelectedUserId, scholarsanchorIsAdminView,
    login, logout, completeSetup, setTheme, setGlobalDarkMode, resetPassword,
    setHistoryOpen, setBoardOpen, setKnowledgeOpen, createConversation,
    loadConversations, setActiveConversation, addMessage, updateTopics,
    togglePinConversation, deleteConversation, setMessageFeedback,
    decrementCredits, updateCredits, addNote, updateNote, deleteNote, requestExpert,
    loadPersonalities, markAsRead, setActiveModel, setSelectedStudentId,
    startExpertSession, closeExpertSession
  ]);

  return (
    <BenchrexContext.Provider value={contextValue}>
      {children}
    </BenchrexContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(BenchrexContext);
  if (!ctx) throw new Error("useApp must be used within ScholarsAnchorProvider");
  return ctx;
};
